import type { LookedUpIdentity } from '@/generated/http-clients/v3ApiSchemas'
import { HStack, RadioGroup, Stack, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

const LAST_LOGIN_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'UTC',
})

type IdentityPickerProps = Readonly<{
  identities: LookedUpIdentity[]
  selectedUserId?: string
  memberUserIds?: readonly string[]
  disabled: boolean
  onSelect: (userId: string) => void
}>

export function IdentityPicker({
  identities,
  selectedUserId,
  memberUserIds,
  disabled,
  onSelect,
}: IdentityPickerProps) {
  const { t } = useTranslation('features', { useSuspense: false })

  function isMember(identity: LookedUpIdentity): boolean {
    return memberUserIds?.includes(identity.userId) === true
  }

  function lastLoginText(identity: LookedUpIdentity): string {
    if (!identity.lastLogin) {
      return String(t('orgDetails.members.addMembers.picker.neverLoggedIn'))
    }
    return String(
      t('orgDetails.members.addMembers.picker.lastLogin', {
        date: LAST_LOGIN_FORMAT.format(new Date(identity.lastLogin)),
      }),
    )
  }

  return (
    <Stack gap={2}>
      <Text fontSize="sm" fontWeight="medium">
        {t('orgDetails.members.addMembers.picker.label')}
      </Text>
      <RadioGroup.Root
        size="sm"
        aria-label={String(t('orgDetails.members.addMembers.picker.label'))}
        value={selectedUserId ?? null}
        disabled={disabled}
        onValueChange={(details) => {
          if (details.value) {
            onSelect(details.value)
          }
        }}
      >
        <Stack gap={2}>
          {identities.map((identity) => (
            <RadioGroup.Item
              key={identity.userId}
              value={identity.userId}
              disabled={disabled || !identity.selectable || isMember(identity)}
              alignItems="flex-start"
            >
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText>
                <Stack gap={0}>
                  <Text fontWeight="medium">
                    {identity.connection ?? identity.type}
                  </Text>
                  <HStack
                    gap={2}
                    flexWrap="wrap"
                    fontSize="xs"
                    color="text.text-subtle"
                  >
                    <Text>{identity.type}</Text>
                    <Text>
                      {identity.emailVerified
                        ? t('orgDetails.members.addMembers.picker.verified')
                        : t('orgDetails.members.addMembers.picker.unverified')}
                    </Text>
                    <Text>{lastLoginText(identity)}</Text>
                  </HStack>
                  {!identity.selectable && (
                    <Text fontSize="xs" color="fg.error">
                      {t('orgDetails.members.addMembers.picker.notSelectable')}
                    </Text>
                  )}
                  {isMember(identity) && (
                    <Text fontSize="xs" color="fg.error">
                      {t('orgDetails.members.addMembers.picker.alreadyMember')}
                    </Text>
                  )}
                </Stack>
              </RadioGroup.ItemText>
            </RadioGroup.Item>
          ))}
        </Stack>
      </RadioGroup.Root>
    </Stack>
  )
}
